import { useCallback, useEffect, useRef, useState } from "react";
import {
  createSpeechRecognition,
  isSpeechRecognitionSupported,
  mergeDictationText
} from "../utils/speechDictation.js";

/**
 * Push-to-talk dictation for a controlled text field.
 * Final chunks append permanently; interim preview is shown without committing until final.
 *
 * @param {object} options
 * @param {string} options.value
 * @param {(next: string) => void} options.onChange
 * @param {number} [options.maxChars=300]
 */
export function useSpeechDictation({ value, onChange, maxChars = 300 }) {
  const supported = isSpeechRecognitionSupported();
  const [listening, setListening] = useState(false);
  const [error, setError] = useState("");

  const valueRef = useRef(value);
  const committedRef = useRef(value);
  const recognitionRef = useRef(null);
  const listeningRef = useRef(false);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const teardown = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    listeningRef.current = false;
    setListening(false);
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  const setUserText = useCallback(
    (next) => {
      const clipped = String(next || "").slice(0, maxChars);
      committedRef.current = clipped;
      onChange(clipped);
    },
    [maxChars, onChange]
  );

  const start = useCallback(() => {
    if (!supported || listeningRef.current) return;
    setError("");
    committedRef.current = valueRef.current;

    const recognition = createSpeechRecognition({
      onResult: ({ transcript, isFinal }) => {
        if (isFinal) {
          const next = mergeDictationText(committedRef.current, transcript, maxChars);
          committedRef.current = next;
          onChange(next);
        } else {
          const preview = mergeDictationText(committedRef.current, transcript, maxChars);
          onChange(preview);
        }
      },
      onError: (message) => {
        if (message === "aborted" || message === "no-speech") return;
        setError(typeof message === "string" ? message : "Dictation is unavailable right now. You can keep typing.");
      },
      onEnd: () => {
        listeningRef.current = false;
        setListening(false);
        recognitionRef.current = null;
        onChange(committedRef.current.slice(0, maxChars));
      }
    });

    if (!recognition) {
      setError("Dictation is not available in this browser. You can keep typing.");
      return;
    }

    recognitionRef.current = recognition;
    listeningRef.current = true;
    setListening(true);
    recognition.start();
  }, [supported, maxChars, onChange]);

  const stop = useCallback(() => {
    if (!recognitionRef.current) {
      listeningRef.current = false;
      setListening(false);
      return;
    }
    recognitionRef.current.stop();
  }, []);

  const toggle = useCallback(() => {
    if (listeningRef.current) stop();
    else start();
  }, [start, stop]);

  return {
    supported,
    listening,
    error,
    clearError: () => setError(""),
    setUserText,
    start,
    stop,
    toggle
  };
}
